/**
 * ReviewStep Component
 *
 * Final review step for FORGE Detailed Mode
 * Shows summary of all entered data with edit capabilities
 */

import React from 'react';
import type { DetailedFormData, DetailedModeStep, RequirementType } from '../types/forge';
import styles from '../styles/forge.module.css';

interface ReviewStepProps {
  formData: DetailedFormData;
  requirementType: RequirementType;
  serviceId: string;
  onEdit: (step: DetailedModeStep) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  formData,
  requirementType,
  serviceId,
  onEdit,
  onSubmit,
  isSubmitting,
}) => {
  const formatType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatPriority = (priority: string) => {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  return (
    <div className={styles.forgeReview}>
      <h3 className={styles.forgeReviewTitle}>Review Your Submission</h3>

      {/* Summary Section */}
      <div className={styles.forgeReviewSection}>
        <div className={styles.forgeReviewSectionHeader}>
          <h4 className={styles.forgeReviewSectionTitle}>Summary</h4>
          <button
            onClick={() => onEdit('details')}
            className={styles.forgeReviewEditButton}
            type="button"
          >
            Edit
          </button>
        </div>
        <dl className={styles.forgeReviewDl}>
          <dt>Type</dt>
          <dd className={styles.forgeReviewTypeBadge}>{formatType(requirementType)}</dd>
          <dt>Title</dt>
          <dd>{formData.title}</dd>
          <dt>Priority</dt>
          <dd className={`${styles.forgeReviewPriority} ${styles[`forgeReviewPriority${formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1)}`]}`}>
            {formatPriority(formData.priority)}
          </dd>
          <dt>Description</dt>
          <dd className={styles.forgeReviewDescription}>{formData.description}</dd>
        </dl>
      </div>

      {/* Optional Fields Section */}
      {(formData.businessValue || formData.technicalDetails || formData.acceptanceCriteria || formData.dependencies) && (
        <div className={styles.forgeReviewSection}>
          <div className={styles.forgeReviewSectionHeader}>
            <h4 className={styles.forgeReviewSectionTitle}>Additional Details</h4>
            <button
              onClick={() => onEdit('details')}
              className={styles.forgeReviewEditButton}
              type="button"
            >
              Edit
            </button>
          </div>
          <dl className={styles.forgeReviewDl}>
            {formData.businessValue && (
              <>
                <dt>Business Value</dt>
                <dd className={styles.forgeReviewDescription}>{formData.businessValue}</dd>
              </>
            )}
            {formData.technicalDetails && (
              <>
                <dt>Technical Details</dt>
                <dd className={styles.forgeReviewDescription}>{formData.technicalDetails}</dd>
              </>
            )}
            {formData.acceptanceCriteria && (
              <>
                <dt>Acceptance Criteria</dt>
                <dd className={styles.forgeReviewDescription}>{formData.acceptanceCriteria}</dd>
              </>
            )}
            {formData.dependencies && (
              <>
                <dt>Dependencies</dt>
                <dd className={styles.forgeReviewDescription}>{formData.dependencies}</dd>
              </>
            )}
          </dl>
        </div>
      )}

      {/* Context Preview Section */}
      <div className={styles.forgeReviewSection}>
        <h4 className={styles.forgeReviewSectionTitle}>Context</h4>
        <dl className={styles.forgeReviewDl}>
          <dt>Service</dt>
          <dd>{serviceId}</dd>
          <dt>Page URL</dt>
          <dd className={styles.forgeReviewUrl}>{window.location.href}</dd>
          <dt>Timestamp</dt>
          <dd>{new Date().toLocaleString()}</dd>
        </dl>
      </div>

      {/* Actions */}
      <div className={styles.formActions}>
        <button
          onClick={() => onEdit('details')}
          className={`${styles.button} ${styles.buttonSecondary}`}
          disabled={isSubmitting}
          type="button"
        >
          Back
        </button>
        <button
          onClick={onSubmit}
          className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonSuccess}`}
          disabled={isSubmitting}
          data-testid="forge-final-submit"
          type="button"
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </div>
  );
};

export default ReviewStep;
